// P5.4 — Lab Welcome Wizard.
//
// Premium, premium-light onboarding overlay shown the first time a user
// enters a Lab. Reusable across all current and future Labs — pass a
// `labKey` and the component reads:
//   • content (capabilities, dataUsed, benefits, permissions) from
//     i18n `labWizard.labs.<labKey>`
//   • per-user "already seen" state from /api/users/me/lab-onboarding
//   • profile-completion gaps from /api/users/me/completion
//
// Pepe brief (Feb 2026):
//   • Explanation of capabilities · data used · benefits · gaps ·
//     completeness progress · permissions · elegant assistant feel.
//   • Mobile-first · TEAM OS visual language · 0 production impact.
//   • Avatar slot reserved but EMPTY today (Owly paused — flip
//     `AVATAR_ENABLED=true` once approved).
//
// Skip / Get started both mark the wizard seen so we don't nag the user;
// they can re-open it from anywhere via the "Help" affordance in the Lab.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, X, ChevronRight, ChevronLeft, Sparkles, Database,
  Trophy, ListChecks, ShieldCheck, ArrowRight,
} from "lucide-react";

// Toggle flips ON when José approves the final TeamOwlAvatar variant.
const AVATAR_ENABLED = false;

const STEPS = ["welcome", "capabilities", "data", "benefits", "gaps", "permissions"];

const StepIcon = ({ step, className = "w-5 h-5" }) => {
  const map = {
    welcome:      Sparkles,
    capabilities: ListChecks,
    data:         Database,
    benefits:     Trophy,
    gaps:         CheckCircle2,
    permissions:  ShieldCheck,
  };
  const Icon = map[step] || Sparkles;
  return <Icon className={className} />;
};

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed">
    <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
    <span>{children}</span>
  </li>
);

const LabWelcomeWizard = ({ labKey, version = "v1", onClose }) => {
  const { t, tNode } = useLang();
  const [step, setStep] = useState(0);
  const [completion, setCompletion] = useState(null);
  const [wishlistPrefs, setWishlistPrefs] = useState(null);

  useEffect(() => {
    api.get("/users/me/completion").then((r) => setCompletion(r.data)).catch(() => setCompletion(null));
    // P11-v2 — read user's wishlist preferences to decide whether to show
    // the conditional Marketplace teaser chip in the Benefits step.
    api.get("/users/me/settings")
      .then((r) => setWishlistPrefs(r.data?.wishlist_preferences || null))
      .catch(() => setWishlistPrefs(null));
  }, []);

  // P11-v2 — derived flag: show teaser iff the user has saved at least
  // one brand of interest OR one desired product category.
  const showMarketplaceCta = !!(
    wishlistPrefs &&
    ((wishlistPrefs.equipment_brands_of_interest || []).length > 0 ||
      (wishlistPrefs.desired_product_categories || []).length > 0)
  );

  // i18n content for the active Lab. `tNode` returns the raw object/array
  // leaf (`t` only resolves strings — would return the literal path here).
  // Falls back to a minimal generic shape so an unknown labKey doesn't
  // crash the wizard.
  const content = useMemo(() => {
    const block = (typeof tNode === "function" ? tNode(`labWizard.labs.${labKey}`) : null);
    if (block && typeof block === "object") return block;
    return {
      name: labKey,
      tagline: "",
      welcomeHeadline: labKey,
      welcomeBody: "",
      capabilities: [],
      dataUsed: [],
      benefits: [],
      permissions: [],
    };
  }, [labKey, tNode]);

  const totalSteps = STEPS.length;
  const current = STEPS[step];

  const markSeen = async () => {
    try {
      await api.put(`/users/me/lab-onboarding/${labKey}`, { version });
    } catch {
      // Non-blocking — if persistence fails, the user just sees the
      // wizard again next visit. Better that than crash the dismiss.
    }
    onClose?.();
  };

  const goNext = () => {
    if (step < totalSteps - 1) setStep(step + 1);
    else markSeen();
  };
  const goBack = () => setStep(Math.max(0, step - 1));

  // ---------- Step content ----------
  const renderStep = () => {
    if (current === "welcome") {
      return (
        <div className="text-center" data-testid="lab-wizard-step-welcome">
          {AVATAR_ENABLED && (
            <div className="mx-auto w-24 h-24 rounded-full bg-emerald-50 border border-emerald-100 mb-4" />
          )}
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{content.welcomeHeadline}</h2>
          {content.tagline && (
            <p className="mt-1 text-sm text-emerald-700 font-medium">{content.tagline}</p>
          )}
          <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-md mx-auto">{content.welcomeBody}</p>
        </div>
      );
    }
    if (current === "capabilities") {
      return (
        <div data-testid="lab-wizard-step-capabilities">
          <div className="flex items-center gap-2 mb-3">
            <StepIcon step="capabilities" className="w-5 h-5 text-emerald-700" />
            <h3 className="text-lg font-semibold text-slate-900">{t("labWizard.capsTitle")}</h3>
          </div>
          <ul className="space-y-2">{content.capabilities.map((c, i) => <Bullet key={i}>{c}</Bullet>)}</ul>
        </div>
      );
    }
    if (current === "data") {
      return (
        <div data-testid="lab-wizard-step-data">
          <div className="flex items-center gap-2 mb-3">
            <StepIcon step="data" className="w-5 h-5 text-emerald-700" />
            <h3 className="text-lg font-semibold text-slate-900">{t("labWizard.dataTitle")}</h3>
          </div>
          <ul className="space-y-2">{content.dataUsed.map((d, i) => <Bullet key={i}>{d}</Bullet>)}</ul>
          <p className="mt-4 text-xs text-slate-500 italic">{t("labWizard.dataNote")}</p>
        </div>
      );
    }
    if (current === "benefits") {
      return (
        <div data-testid="lab-wizard-step-benefits">
          <div className="flex items-center gap-2 mb-3">
            <StepIcon step="benefits" className="w-5 h-5 text-emerald-700" />
            <h3 className="text-lg font-semibold text-slate-900">{t("labWizard.benefitsTitle")}</h3>
          </div>
          {showMarketplaceCta && (
            <div
              className="mb-3 rounded-lg border border-emerald-100 bg-emerald-50/60 px-3 py-2.5 text-xs text-emerald-900 leading-relaxed"
              data-testid="lab-wizard-benefits-marketplace-cta"
            >
              {t("labWizard.benefitsMarketplaceCta")}
            </div>
          )}
          <ul className="space-y-2">{content.benefits.map((b, i) => <Bullet key={i}>{b}</Bullet>)}</ul>
        </div>
      );
    }
    if (current === "gaps") {
      const pct = Math.max(0, Math.min(100, completion?.percentage ?? 0));
      const hintKey = completion?.recommended_next?.hint_key;
      const hint = hintKey ? t(hintKey) : null;
      return (
        <div data-testid="lab-wizard-step-gaps">
          <div className="flex items-center gap-2 mb-3">
            <StepIcon step="gaps" className="w-5 h-5 text-emerald-700" />
            <h3 className="text-lg font-semibold text-slate-900">{t("labWizard.gapsTitle")}</h3>
          </div>
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-sm text-slate-700">{t("settings.completion.title")}</span>
              <span className="text-sm font-semibold text-emerald-900 tabular-nums">{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-white overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} data-testid="lab-wizard-gaps-bar" />
            </div>
            {hint && <p className="mt-3 text-sm text-slate-700">{hint}</p>}
            <p className="mt-2 text-xs text-slate-500">{t("labWizard.gapsHelp")}</p>
          </div>
          <Link
            to="/app/settings"
            className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-emerald-700 hover:text-emerald-800"
            onClick={markSeen}
            data-testid="lab-wizard-gaps-settings-link"
          >
            {t("labWizard.gapsCta")} <ArrowRight className="w-3.5 h-3.5 rtl:rotate-180" />
          </Link>
        </div>
      );
    }
    if (current === "permissions") {
      return (
        <div data-testid="lab-wizard-step-permissions">
          <div className="flex items-center gap-2 mb-3">
            <StepIcon step="permissions" className="w-5 h-5 text-emerald-700" />
            <h3 className="text-lg font-semibold text-slate-900">{t("labWizard.permsTitle")}</h3>
          </div>
          <ul className="space-y-2">{content.permissions.map((p, i) => <Bullet key={i}>{p}</Bullet>)}</ul>
          <p className="mt-4 text-xs text-slate-500 italic">{t("labWizard.permsNote")}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="lab-wizard"
    >
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={markSeen} data-testid="lab-wizard-backdrop" />
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
            <StepIcon step={current} className="w-3.5 h-3.5" />
            {t(`labWizard.steps.${current}`)} · {t("labWizard.step")} {step + 1} {t("labWizard.of")} {totalSteps}
          </div>
          <button
            type="button"
            onClick={markSeen}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Close"
            data-testid="lab-wizard-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* Progress dots */}
        <div className="flex gap-1.5 px-4 pt-3">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`flex-1 h-1 rounded-full transition ${i <= step ? "bg-emerald-500" : "bg-slate-200"}`}
            />
          ))}
        </div>
        {/* Body */}
        <div className="p-5 sm:p-6 overflow-y-auto flex-1" data-testid="lab-wizard-body">
          {renderStep()}
        </div>
        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-slate-100">
          <button
            type="button"
            onClick={markSeen}
            className="text-sm text-slate-500 hover:text-slate-700"
            data-testid="lab-wizard-skip"
          >
            {t("labWizard.skip")}
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="ghost" size="sm" onClick={goBack} data-testid="lab-wizard-back">
                <ChevronLeft className="w-4 h-4 me-1 rtl:rotate-180" /> {t("labWizard.back")}
              </Button>
            )}
            <Button
              size="sm"
              onClick={goNext}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="lab-wizard-next"
            >
              {step === totalSteps - 1 ? t("labWizard.finish") : t("labWizard.next")}
              {step < totalSteps - 1 && <ChevronRight className="w-4 h-4 ms-1 rtl:rotate-180" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabWelcomeWizard;
